import {
  Stepper as UIKitStepper,
  Step,
  useSteps,
  StepIndicator,
  StepStatus,
  StepTitle,
  StepIcon,
  Text,
  Box,
} from '@everlywell/ui-kit';
import React, { useEffect } from 'react';

// Types ----------

export interface StepProps {
  title: string;
}

export interface StepperProps {
  steps: StepProps[];
  activeStepIndex: number;
  hideIndicators?: boolean;
  stepperTheme?: {
    light?: string;
    base?: string;
  };
}

// Stepper ------

const INACTIVE_STEP_WIDTH = 60;
const STEP_SIDE_PADDING = 6;

const Stepper: React.FC<StepperProps> = (props) => {
  const {
    activeStepIndex,
    hideIndicators = false,
    steps,
    stepperTheme = {
      light: 'eggplant.light',
      base: 'eggplant.base',
    },
  } = props;

  const { activeStep, setActiveStep } = useSteps({
    index: activeStepIndex,
    count: steps.length,
  });

  useEffect(() => {
    setActiveStep(activeStepIndex);
  }, [activeStepIndex, setActiveStep]);

  return (
    <Box position="relative" width="100%" data-testid="stepper">
      <UIKitStepper
        index={activeStep}
        width="100%"
        display="flex"
        flexWrap="nowrap"
      >
        {steps.map((step, index) => {
          const isActiveStep = activeStep === index;
          const isFirstStep = index === 0;
          const isLastStep = index === steps.length - 1;
          const isCompletedStep = activeStep > index;

          let stepWidth = `${INACTIVE_STEP_WIDTH}px`;
          const activeStepWidth = `calc(100% - ((${
            INACTIVE_STEP_WIDTH * (steps.length - 1)
          }px) - (${steps.length - 1} * ${STEP_SIDE_PADDING * 2}px)))`;

          if (isActiveStep) {
            stepWidth = activeStepWidth;
          } else if (isFirstStep || isLastStep) {
            stepWidth = `${INACTIVE_STEP_WIDTH - STEP_SIDE_PADDING}px`;
          }

          return (
            <Box
              width={stepWidth}
              opacity={isCompletedStep || isActiveStep ? 1 : 0.2}
              paddingRight={isLastStep ? '0' : '6px'}
              paddingLeft={isFirstStep ? '0' : '6px'}
              transition="width 0.4s ease-in-out, opacity 0.4s ease-in-out"
              key={`step-${index}`}
            >
              <Box
                paddingTop={hideIndicators ? 2 : 5}
                position="relative"
                width="100%"
              >
                <Box
                  width="100%"
                  position="absolute"
                  top="0"
                  backgroundColor={
                    isCompletedStep ? stepperTheme.light : stepperTheme.base
                  }
                  height={hideIndicators ? '4px' : '8px'}
                  borderRadius="4"
                  left="0"
                />
                <Step key={index} data-testid="stepper-step">
                  {!hideIndicators ? (
                    <Box data-testid="stepper-indicator">
                      <StepIndicator>
                        <StepStatus
                          complete={
                            !isActiveStep ? (
                              <StepIcon
                                background={stepperTheme.light}
                                color="white"
                                borderRadius="50%"
                                padding="3px"
                                width="20px"
                                height="20px"
                                display="block"
                              />
                            ) : null
                          }
                        />
                      </StepIndicator>
                      {!isCompletedStep ? (
                        <StepTitle>
                          <Text
                            color={
                              isCompletedStep
                                ? stepperTheme.light
                                : stepperTheme.base
                            }
                          >
                            {step.title}
                          </Text>
                        </StepTitle>
                      ) : null}
                    </Box>
                  ) : null}
                </Step>
              </Box>
            </Box>
          );
        })}
      </UIKitStepper>
    </Box>
  );
};

// Export ---------

export default React.memo(Stepper);
